import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Divider,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import logo from "../assets/Nikao_Pulse_Asset_Register_Logo.png";
import { useAuthentication } from "nikao-authentication";

const Login = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  const { login, loginWithToken } = useAuth();
  const { login: msalLogin, getIdToken } = useAuthentication({
    scopes: ["openid"],
  });
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleMicrosoftLogin = async () => {
    setLoading(true);
    setLoginError("");

    try {
      await msalLogin();
      const idToken = getIdToken();

      if (!idToken) {
        setLoginError("Could not get Microsoft sign-in token.");
        return;
      }

      const result = await loginWithToken(idToken);

      if (result.success) {
        navigate("/");
      } else {
        setLoginError(result.message);
      }
    } catch (err) {
      const message =
        err.message || "Sign in with Microsoft failed. Please try again.";
      setLoginError(message);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const result = await login(formData.username, formData.password);

      if (result.success) {
        navigate("/");
      } else {
        setLoginError(result.error);
      }
    } catch (error) {
      setLoginError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Pulse Asset Register - Powered by Nikao"
            sx={{
              width: "100%",
              maxWidth: "280px",
              height: "auto",
              mb: 3,
              display: "block",
            }}
          />
          <Typography
            component="h2"
            variant="h6"
            color="textSecondary"
            gutterBottom
          >
            Sign in to your account
          </Typography>

          {loginError && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {loginError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={formData.username}
              onChange={handleChange}
              error={!!errors.username}
              helperText={errors.username}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Sign In"}
            </Button>
            <Divider sx={{ my: 2 }}>OR</Divider>
            <Button
              type="button"
              fullWidth
              variant="outlined"
              sx={{ mt: 1, mb: 2, py: 1.5 }}
              disabled={loading}
              onClick={handleMicrosoftLogin}
            >
              Sign in with Microsoft
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
